import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreatePoliceVerificationModal = ({ isOpen, onClose, onSuccess, token }) => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [formData, setFormData] = useState({
    tenant_id: '',
    owner_name: '',
    owner_mobile: '',
    owner_occupation: '',
    owner_address: '',
    tenant_relation_type: 'Son',
    tenant_relative_name: '',
    tenant_relative_name_1: '',
    tenant_relative_name_2: ''
  });
  const [selectedTenant, setSelectedTenant] = useState(null);

  const fetchUserProfile = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/settings/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.name || response.data.business_name) {
        setFormData(prev => ({
          ...prev,
          owner_name: response.data.name || response.data.business_name || '',
          owner_address: response.data.address || ''
        }));
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    } finally {
      setLoadingProfile(false);
    }
  }, [token]);

  const fetchTenants = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/tenants?status=active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTenants(response.data);
    } catch (error) {
      console.error('Error fetching tenants:', error);
    }
  }, [token]);

  useEffect(() => {
    if (isOpen) {
      fetchUserProfile();
      fetchTenants();
    }
  }, [isOpen, fetchUserProfile, fetchTenants]);

  useEffect(() => {
    if (formData.tenant_id) {
      const tenant = tenants.find(t => t.id === formData.tenant_id);
      setSelectedTenant(tenant);
    }
  }, [formData.tenant_id, tenants]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/police-verification`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast({
        title: "Verification Created!",
        description: "Police verification request has been generated successfully"
      });

      onSuccess();
      onClose();
      resetForm();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to create verification",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  function resetForm() {
    setFormData({
      tenant_id: '',
      owner_name: '',
      owner_mobile: '',
      owner_occupation: '',
      owner_address: '',
      tenant_relation_type: 'Son',
      tenant_relative_name: '',
      tenant_relative_name_1: '',
      tenant_relative_name_2: ''
    });
    setSelectedTenant(null);
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Police Verification Request</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label>Select Tenant *</Label>
              <select
                value={formData.tenant_id}
                onChange={(e) => setFormData({...formData, tenant_id: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select Tenant</option>
                {tenants.map(tenant => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.name} - {tenant.phone}
                  </option>
                ))}
              </select>
            </div>

            {selectedTenant && (
              <div className="col-span-2 p-4 bg-blue-50 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-2">Tenant Details</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <p><span className="font-medium">Name:</span> {selectedTenant.name}</p>
                  <p><span className="font-medium">Phone:</span> {selectedTenant.phone}</p>
                  <p><span className="font-medium">Email:</span> {selectedTenant.email || 'N/A'}</p>
                  <p><span className="font-medium">Status:</span> {selectedTenant.status}</p>
                </div>
              </div>
            )}

            <div className="col-span-2">
              <Label>Owner/Landlord Name (मालिक का नाम) *</Label>
              <Input
                value={formData.owner_name}
                onChange={(e) => setFormData({...formData, owner_name: e.target.value})}
                placeholder="Enter owner name"
                required
              />
              {loadingProfile && <p className="text-xs text-gray-500 mt-1">Loading from profile...</p>}
              {!loadingProfile && formData.owner_name && <p className="text-xs text-green-600 mt-1">✓ Auto-filled from your profile</p>}
            </div>

            <div className="col-span-2">
              <Label>Owner Mobile Number (मोबाइल नंबर) *</Label>
              <Input
                value={formData.owner_mobile}
                onChange={(e) => setFormData({...formData, owner_mobile: e.target.value})}
                placeholder="Enter owner mobile number"
                required
              />
            </div>

            <div className="col-span-2">
              <Label>Owner Occupation (व्यवसाय) *</Label>
              <Input
                value={formData.owner_occupation}
                onChange={(e) => setFormData({...formData, owner_occupation: e.target.value})}
                placeholder="Enter owner occupation"
                required
              />
            </div>

            <div className="col-span-2">
              <Label>Owner Address (पता) *</Label>
              <textarea
                value={formData.owner_address}
                onChange={(e) => setFormData({...formData, owner_address: e.target.value})}
                placeholder="Enter owner address"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="3"
                required
              />
              {!loadingProfile && formData.owner_address && <p className="text-xs text-green-600 mt-1">✓ Auto-filled from your profile</p>}
            </div>

            <div className="col-span-2">
              <Label>Tenant's Father/Guardian Name (रिश्तेदार का नाम) *</Label>
              <Input
                value={formData.tenant_relative_name}
                onChange={(e) => setFormData({...formData, tenant_relative_name: e.target.value})}
                placeholder="Enter father's or guardian's name"
                required
              />
              <p className="text-xs text-gray-500 mt-1">Enter the tenant's father, mother, or guardian name</p>
            </div>

            <div>
              <Label>Relation Type (संबंध प्रकार) *</Label>
              <select
                value={formData.tenant_relation_type}
                onChange={(e) => setFormData({...formData, tenant_relation_type: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="Son">Son (पुत्र)</option>
                <option value="Daughter">Daughter (पुत्री)</option>
                <option value="Wife">Wife (पत्नी)</option>
                <option value="Husband">Husband (पति)</option>
                <option value="Father">Father (पिता)</option>
                <option value="Mother">Mother (माता)</option>
              </select>
            </div>

            <div>
              <Label>Additional Relative Name 1 (अतिरिक्त रिश्तेदार 1)</Label>
              <Input
                value={formData.tenant_relative_name_1}
                onChange={(e) => setFormData({...formData, tenant_relative_name_1: e.target.value})}
                placeholder="Optional"
              />
            </div>

            <div className="col-span-2">
              <Label>Additional Relative Name 2 (अतिरिक्त रिश्तेदार 2)</Label>
              <Input
                value={formData.tenant_relative_name_2}
                onChange={(e) => setFormData({...formData, tenant_relative_name_2: e.target.value})}
                placeholder="Optional"
              />
            </div>
          </div>

          <div className="flex gap-4 pt-4">
            <Button type="submit" className="flex-1 bg-red-600 hover:bg-red-700" disabled={loading}>
              {loading ? 'Creating...' : 'Create Verification Request'}
            </Button>
            <Button type="button" onClick={onClose} variant="outline" className="flex-1">
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreatePoliceVerificationModal;